/**
 * contactLinkTracker.ts — single delegated document listener that fires
 * GA4 + Google Ads events for every tel: / sms: / mailto: click on the page.
 *
 * Contract:
 *   - One canonical GA4 event per click:
 *       tel:    click_to_call
 *       sms:    click_to_text
 *       mailto: click_to_email
 *   - One Ads phone-click conversion for tel: (label PHONE).
 *   - One Ads email-click conversion for mailto: (label EMAIL).
 *   - No PII in GA4 parameters — link_url is redacted to just the scheme,
 *     `link_destination` is a stable category ("business_phone" | "other_phone"
 *     | "email" | "sms").
 *
 * The Growth Hub phoneClickNotifier still runs separately (it sends the
 * mailbox notification and does NOT push Google events), so a tel: click
 * fires exactly one Google phone-click conversion.
 */

import {
  trackGoogleEvent,
  trackGooglePhoneConversion,
  trackGoogleEmailConversion,
} from './googleTracking';

const INSTALL_FLAG = '__OBC_CONTACT_LINK_TRACKER_INSTALLED__';
const BUSINESS_PHONE_DIGITS = '12399194485';

function digitsOnly(v: string): string {
  return (v || '').replace(/\D/g, '');
}

function isBusinessPhone(raw: string): boolean {
  const d = digitsOnly(raw);
  if (!d) return false;
  const n = d.length === 10 ? `1${d}` : d;
  return n === BUSINESS_PHONE_DIGITS;
}

function categorizeTel(raw: string): 'business_phone' | 'other_phone' {
  return isBusinessPhone(raw) ? 'business_phone' : 'other_phone';
}

function placementFor(anchor: HTMLElement | null): string | undefined {
  if (!anchor) return undefined;
  return (
    anchor.getAttribute('data-track-cta') ||
    anchor.getAttribute('data-placement') ||
    anchor.getAttribute('aria-label') ||
    (anchor.textContent || '').trim().slice(0, 60) ||
    undefined
  );
}

// Debounce: same scheme+destination clicked twice in <2.5s only fires once.
const RECENT: Map<string, number> = new Map();
function recentGuard(key: string): boolean {
  const now = Date.now();
  const last = RECENT.get(key) || 0;
  if (now - last < 2500) return false;
  RECENT.set(key, now);
  return true;
}

export function installContactLinkTracker(): void {
  if (typeof window === 'undefined') return;
  const w = window as any;
  if (w[INSTALL_FLAG]) return;
  w[INSTALL_FLAG] = true;

  document.addEventListener(
    'click',
    (evt) => {
      const target = evt.target as Element | null;
      if (!target || !target.closest) return;
      const anchor = target.closest(
        'a[href^="tel:"],a[href^="sms:"],a[href^="mailto:"]',
      ) as HTMLAnchorElement | null;
      if (!anchor) return;

      const href = anchor.getAttribute('href') || '';
      const page_path = window.location.pathname;
      const placement = placementFor(anchor);

      if (href.startsWith('tel:')) {
        const raw = href.slice(4);
        const key = `tel:${digitsOnly(raw)}:${page_path}`;
        if (!recentGuard(key)) return;
        const destination = categorizeTel(raw);
        trackGoogleEvent('click_to_call', {
          link_url: 'tel:',
          link_destination: destination,
          page_path,
          placement,
        });
        // Ads phone-click conversion (dedupe handled inside googleTracking)
        trackGooglePhoneConversion({
          form_id: 'contact_link',
          form_name: placement || 'tel_click',
          page_path,
          value: 0,
          currency: 'USD',
        });
        return;
      }

      if (href.startsWith('sms:')) {
        const raw = href.slice(4);
        const key = `sms:${digitsOnly(raw)}:${page_path}`;
        if (!recentGuard(key)) return;
        trackGoogleEvent('click_to_text', {
          link_url: 'sms:',
          link_destination: 'sms',
          page_path,
          placement,
        });
        return;
      }

      if (href.startsWith('mailto:')) {
        const key = `mailto:${page_path}:${placement || ''}`;
        if (!recentGuard(key)) return;
        trackGoogleEvent('click_to_email', {
          link_url: 'mailto:',
          link_destination: 'email',
          page_path,
          placement,
        });
        trackGoogleEmailConversion({
          form_id: 'contact_link',
          form_name: placement || 'mailto_click',
          page_path,
          value: 0,
          currency: 'USD',
        });
      }
    },
    { capture: true },
  );
}
